/**
 * PublicLayout — shared marketing shell for public/SEO pages.
 * Renders a marketing navbar + footer around <Outlet />.
 * Uses the same warm paper palette as the existing LandingPage.
 */
import { Link, Outlet, useLocation } from "react-router-dom";
import { Zap, Menu, X } from "lucide-react";
import { useTenant } from "@/stores/tenantStore.tsx";
import { useState } from "react";

// Design tokens — same as LandingPage
const T = {
  ink:     "#0a0a0a",
  inkSoft: "#6b6b6b",
  rule:    "#e9e6df",
  rule2:   "#d8d4c8",
  paper:   "#faf8f3",
  paper2:  "#f3efe5",
  accent:  "#ff5b1f",
};

const NAV_LINKS = [
  { to: "/features",     label: "Features" },
  { to: "/how-it-works", label: "How it works" },
  { to: "/pricing",      label: "Pricing" },
  { to: "/use-cases",    label: "Use cases" },
  { to: "/blog",         label: "Blog" },
  { to: "/help",         label: "Help" },
];

export function PublicLayout() {
  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: T.paper,
        color: T.ink,
        fontFamily: '"Inter", system-ui, sans-serif',
        display: "flex",
        flexDirection: "column",
      }}
    >
      <PublicNav />
      <main style={{ flex: 1 }}>
        <Outlet />
      </main>
      <PublicFooter />
    </div>
  );
}

// ── Nav ───────────────────────────────────────────────────────────────────────
function PublicNav() {
  const { tenant, siteName } = useTenant();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header
      className="sticky top-0 z-50"
      style={{
        backgroundColor: "rgba(250,248,243,0.92)",
        backdropFilter: "blur(12px)",
        borderBottom: `1px solid ${T.rule}`,
      }}
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between px-5 md:px-8 py-4">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 flex-shrink-0">
          {tenant?.logoUrl ? (
            <img src={tenant.logoUrl} alt={siteName} className="h-8 w-8 rounded-lg object-contain" />
          ) : (
            <div
              className="flex h-8 w-8 items-center justify-center rounded-lg text-white font-bold text-sm flex-shrink-0"
              style={{ backgroundColor: T.accent }}
            >
              <Zap className="w-4 h-4" />
            </div>
          )}
          <span
            className="font-bold text-[17px] leading-none"
            style={{
              fontFamily: '"Bricolage Grotesque", "Inter", system-ui',
              letterSpacing: "-0.02em",
              color: T.ink,
            }}
          >
            {siteName}
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-7">
          {NAV_LINKS.map(({ to, label }) => {
            const active = location.pathname === to || location.pathname.startsWith(to + "/");
            return (
              <Link
                key={to}
                to={to}
                className="text-[14px] transition-colors hover:opacity-80"
                style={{
                  color: active ? T.ink : T.inkSoft,
                  fontWeight: active ? 600 : 500,
                }}
              >
                {label}
              </Link>
            );
          })}
        </nav>

        {/* CTA + mobile toggle */}
        <div className="flex items-center gap-3">
          <Link to="/login" className="hidden md:inline-block text-[14px] font-medium" style={{ color: T.inkSoft }}>
            Log in
          </Link>
          <Link
            to="/signup"
            className="hidden md:inline-flex items-center gap-2 text-[14px] font-semibold px-5 py-2.5 rounded-lg transition-opacity hover:opacity-90"
            style={{ backgroundColor: T.accent, color: "#fff" }}
          >
            Get started free
          </Link>
          <button
            className="md:hidden p-1"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            onClick={() => setMobileOpen(!mobileOpen)}
            style={{ background: "transparent", border: 0, color: T.ink, cursor: "pointer" }}
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      {mobileOpen && (
        <div
          className="md:hidden"
          style={{ borderTop: `1px solid ${T.rule}`, backgroundColor: T.paper, padding: "12px 20px 20px" }}
        >
          {NAV_LINKS.map(({ to, label }) => (
            <Link
              key={to}
              to={to}
              onClick={() => setMobileOpen(false)}
              className="block py-3 text-[15px] font-medium"
              style={{ color: T.ink, borderBottom: `1px solid ${T.rule}` }}
            >
              {label}
            </Link>
          ))}
          <div className="flex gap-3 pt-4">
            <Link to="/login" className="text-[14px] font-medium py-2" style={{ color: T.inkSoft }}>
              Log in
            </Link>
            <Link
              to="/signup"
              className="text-[14px] font-semibold px-5 py-2.5 rounded-lg"
              style={{ backgroundColor: T.accent, color: "#fff" }}
            >
              Get started free
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────
function PublicFooter() {
  const { tenant, siteName } = useTenant();

  const columns = [
    {
      title: "Product",
      links: [
        { to: "/features", label: "Features" },
        { to: "/how-it-works", label: "How it works" },
        { to: "/pricing", label: "Pricing" },
        { to: "/use-cases", label: "Use cases" },
      ],
    },
    {
      title: "Resources",
      links: [
        { to: "/blog", label: "Blog" },
        { to: "/about", label: "About" },
        { to: "/help", label: "Help Center" },
        { to: "/#faq", label: "FAQ" },
      ],
    },
    {
      title: "Account",
      links: [
        { to: "/login", label: "Log in" },
        { to: "/signup", label: "Sign up" },
        { to: "/billing", label: "Billing" },
      ],
    },
  ];

  return (
    <footer style={{ backgroundColor: T.paper2, borderTop: `1px solid ${T.rule}` }}>
      <div className="mx-auto max-w-6xl px-5 md:px-8 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
          {/* Brand col */}
          <div>
            <Link to="/" className="flex items-center gap-2 mb-4">
              {tenant?.logoUrl ? (
                <img src={tenant.logoUrl} alt={siteName} className="h-7 w-7 rounded-lg object-contain" />
              ) : (
                <div
                  className="flex h-7 w-7 items-center justify-center rounded-lg text-white flex-shrink-0"
                  style={{ backgroundColor: T.accent }}
                >
                  <Zap className="w-3.5 h-3.5" />
                </div>
              )}
              <span
                className="text-[15px] font-bold"
                style={{
                  fontFamily: '"Bricolage Grotesque", "Inter", system-ui',
                  letterSpacing: "-0.02em",
                  color: T.ink,
                }}
              >
                {siteName}
              </span>
            </Link>
            <p className="text-[13px] leading-relaxed" style={{ color: T.inkSoft }}>
              AI-powered faceless video creation. Script to publish in minutes.
            </p>
          </div>

          {columns.map((col) => (
            <div key={col.title}>
              <h4 className="text-[12px] font-semibold uppercase tracking-widest mb-4" style={{ color: T.rule2 }}>
                {col.title}
              </h4>
              <ul className="space-y-2.5">
                {col.links.map((link) => (
                  <li key={link.to}>
                    <Link to={link.to} className="text-[13px] hover:opacity-80 transition-colors" style={{ color: T.inkSoft }}>
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div
          className="flex flex-col md:flex-row items-center justify-between gap-4 pt-8"
          style={{ borderTop: `1px solid ${T.rule}` }}
        >
          <p className="text-[11px] font-mono" style={{ color: T.rule2 }}>
            &copy; {new Date().getFullYear()} {siteName}. All rights reserved.
          </p>
          <div className="flex gap-5 text-[12px]" style={{ color: T.inkSoft }}>
            <Link to="/privacy" className="hover:opacity-80">Privacy Policy</Link>
            <Link to="/terms" className="hover:opacity-80">Terms of Service</Link>
            {/* (B120) plain links, in every public page's raw HTML; (B120-1) D5's names */}
            <Link to="/data-deletion" className="hover:opacity-80">Data deletion</Link>
            <Link to="/contact" className="hover:opacity-80">Contact</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
